import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';

import { PageBase } from '../PageBase';
import { formatTime } from '../forgotPassword/counter';
import { hasLetter, hasMinLength, hasNumber } from '@/utils/passwordValidation';
import TextField from '@/components/textField/TextField';
import Button from '@/components/button/Button';
import { useUserService } from '@/data/services/useUserService';
import { useAuthForgotPasswordStore } from './store/auth';
import { AppError } from '@/utils/AppError';
import { theme } from '@/styles/theme';
import WarningField from '@/components/warningField/WarningField';
import { Success } from '@/components/success/Success';

interface NewPasswordInputs {
  code: string;
  password: string;
  confirmPassword: string;
}

export const CreateNewPassword = () => {
  const { t } = useTranslation(['create_new_password', 'success']);
  const { email } = useAuthForgotPasswordStore();
  const { resendVerificationCode, registerNewPassword } = useUserService();
  const [loading, setLoading] = useState(false);
  const [counter, setCounter] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const [warningMessage, setWarningMessage] = useState('');

  const handleWarningRightButtonAction = () => {
    setShowWarning(false);
  };

  const navigate = useNavigate();

  const schema = yup.object().shape({
    code: yup.string().required(t('required_code')),
    password: yup
      .string()
      .test('minLength', t('min_length_password'), (value) =>
        hasMinLength(value)
      )
      .test('number', t('password_has_number'), (value) => hasNumber(value))
      .test('letter', t('password_has_letter'), (value) => hasLetter(value))
      .required(t('password_required')),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], t('password_not_match'))
      .required(t('password_confirmation_required')),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<NewPasswordInputs>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const password = watch('password', '');

  const handleResend = () => {
    if (canResend && email) {
      resendVerificationCode(email)
        .then(() => {
          setShowWarning(true);
          setWarningMessage(t('code_resent_info', { email }));
        })
        .catch(() => {
          setShowWarning(true);
          setWarningMessage(t('code_resend_error'));
        });
      setCounter(30);
      setCanResend(false);
    }
  };

  const onSubmit = (data: NewPasswordInputs) => {
    if (email) {
      setLoading(true);
      registerNewPassword(email, data.code, data.password)
        .then(() => {
          setIsSuccess(true);
          sessionStorage.setItem('passwordResetSuccess', 'true');
        })
        .catch((error: AppError) => {
          setShowWarning(true);
          setWarningMessage(t(error.message));
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    const successFlag = sessionStorage.getItem('passwordResetSuccess');

    if (successFlag === 'true') {
      const navigationEntries = performance.getEntriesByType(
        'navigation'
      ) as PerformanceNavigationTiming[];

      const isReload =
        navigationEntries.length > 0
          ? navigationEntries[0].type === 'reload'
          : document.visibilityState === 'hidden';

      if (isReload) {
        setRedirecting(true);
        setTimeout(() => {
          sessionStorage.removeItem('passwordResetSuccess');
          navigate('/');
        }, 1000);
      } else {
        setIsSuccess(true);
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (counter > 0) {
      const timer = setTimeout(() => setCounter((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [counter]);

  useEffect(() => {
    const handlePopState = () => navigate('/');

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  if (redirecting) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isSuccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Success
          title={t('success:password_reset_success_title')}
          description={t('success:sign_in_success_description')}
          buttonTitle={t('success:sign_in_success_button')}
          onButtonClick={() => {
            sessionStorage.removeItem('passwordResetSuccess');
            navigate('/');
          }}
        />
      </Box>
    );
  }

  return (
    <PageBase
      title={t('title_forgot_password')}
      onBackClick={() => navigate(-1)}
      paddingInline={0}
    >
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          [theme.breakpoints.down('sm')]: {
            alignItems: 'center',
          },
        }}
      >
        <Box
          sx={{
            [theme.breakpoints.down('sm')]: {
              minWidth: 280,
            },
          }}
        >
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 600,
              color: theme.palette.primary.main,
            }}
          >
            {t('verify_your_email')}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.4rem',
              fontWeight: 400,
            }}
          >
            <Trans
              i18nKey="create_new_password:email_sent_code"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Box>
        <Controller
          name="code"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              title={t('title_code_verification')}
              type="text"
              disabled={loading}
              error={!!errors.code}
              helperText={errors.code?.message}
              placeholder="000000"
              onValueChange={(value) => field.onChange(value)}
              maxLength={6}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  minWidth: 280,
                },
              }}
            />
          )}
        />
        <Box
          sx={{
            [theme.breakpoints.down('sm')]: {
              minWidth: 280,
            },
          }}
        >
          <Typography sx={{ fontSize: '1.2rem' }}>
            &#8226; {t('expiration_time')}
            {counter > 0 ? formatTime(counter) : '00:00'}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.2rem',
            }}
          >
            &#8226; {t('not_received_code')}{' '}
            <span
              onClick={handleResend}
              style={{
                color: canResend
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                cursor: canResend ? 'pointer' : 'not-allowed',
              }}
            >
              {t('resend_code')}
            </span>
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            [theme.breakpoints.down('sm')]: {
              minWidth: 280,
            },
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontWeight: 600,
              }}
            >
              {t('subtitle_create_new_password')}
            </Typography>
          </Box>

          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                title={t('password')}
                hasAdornmentEnd
                error={!!errors.password}
                helperText={errors.password?.message}
                placeholder={t('enter_your_new_password')}
                onValueChange={(value) => field.onChange(value)}
              />
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                title={t('confirm_password')}
                disabled={loading}
                hasAdornmentEnd
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                placeholder={t('confirm_your_new_password')}
                onValueChange={(value) => field.onChange(value)}
              />
            )}
          />
          <Box>
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontWeight: 600,
                paddingBottom: '1rem',
              }}
            >
              {t('password_requirements_title')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasMinLength(password) ? (
                <CheckIcon color="success" />
              ) : (
                <CloseIcon color="error" />
              )}
              <Typography sx={{ fontSize: '1.2rem' }}>
                {t('has_eight_characters')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasNumber(password) ? (
                <CheckIcon color="success" />
              ) : (
                <CloseIcon color="error" />
              )}
              <Typography sx={{ fontSize: '1.2rem' }}>
                {t('has_one_number')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasLetter(password) ? (
                <CheckIcon color="success" />
              ) : (
                <CloseIcon color="error" />
              )}
              <Typography sx={{ fontSize: '1.2rem' }}>
                {t('has_one_letter')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <WarningField
          description={warningMessage}
          isHidden={!showWarning}
          setShowWarning={setShowWarning}
          status="warning"
          rightButtonTitle={t('close')}
          rightButtonAction={handleWarningRightButtonAction}
        />
        <Button
          title={t('btn_create_password')}
          onClick={handleSubmit(onSubmit)}
          disable={!isValid || loading}
          loading={loading}
          sx={{
            [theme.breakpoints.down('sm')]: {
              minWidth: 280,
            },
          }}
        />
      </Box>
    </PageBase>
  );
};
